import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { seoService } from '../services/seoService'
import { applySeoToDocument } from '../utils/applySeoToDocument'

const SeoContext = createContext(null)

/** Títulos de página por ruta (nombre corto para `<title>`). */
const ROUTE_PAGE_LABELS = {
  '/': 'Inicio',
  '/mascotas': 'Mascotas',
  '/citas': 'Citas',
  '/fichas': 'Fichas clínicas',
  '/veterinarios': 'Veterinarios',
  '/clinicas': 'Clínicas',
  '/reportes': 'Reportes',
  '/notificaciones': 'Notificaciones',
  '/usuarios': 'Usuarios',
  '/perfil': 'Perfil',
  '/suscripciones-clinicas': 'Suscripciones',
  '/acceso-mascotas': 'Acceso QR',
  '/configuracion': 'Configuración',
  '/configuracion/roles': 'Roles y usuarios',
  '/configuracion/condiciones-salud': 'Condiciones de salud',
  '/configuracion/push-condicion-salud': 'Push condición salud',
  '/configuracion/seo': 'SEO',
  '/configuracion/paises': 'Países',
  '/configuracion/sistema': 'Sistema',
}

function resolvePageLabel(pathname) {
  if (!pathname) return null
  if (ROUTE_PAGE_LABELS[pathname]) return ROUTE_PAGE_LABELS[pathname]
  if (pathname.startsWith('/mascotas/') && pathname !== '/mascotas') return 'Mascota'
  if (pathname.startsWith('/citas/') && pathname !== '/citas') return 'Cita'
  if (pathname.startsWith('/fichas/') && pathname !== '/fichas') return 'Ficha clínica'
  if (pathname.startsWith('/configuracion')) return ROUTE_PAGE_LABELS[pathname] || 'Configuración'
  return null
}

export function SeoProvider({ children }) {
  const location = useLocation()
  const [seo, setSeo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadSeo = useCallback(async () => {
    try {
      setError(null)
      const resp = await seoService.getPublic()
      if (resp?.success && resp.data) {
        setSeo(resp.data)
      } else {
        setSeo(null)
      }
    } catch (e) {
      console.warn('[SEO] No se pudo cargar configuración:', e?.message || e)
      setError(e?.message || 'SEO')
      setSeo(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSeo()
  }, [loadSeo])

  useEffect(() => {
    if (!seo) return
    const pageLabel = resolvePageLabel(location.pathname)
    applySeoToDocument(seo, { pageLabel, pathname: location.pathname })
  }, [seo, location.pathname])

  const refreshSeo = useCallback(async () => {
    setLoading(true)
    await loadSeo()
  }, [loadSeo])

  const value = useMemo(
    () => ({
      seo,
      loading,
      error,
      refreshSeo,
    }),
    [seo, loading, error, refreshSeo]
  )

  return <SeoContext.Provider value={value}>{children}</SeoContext.Provider>
}

export function useSeo() {
  const ctx = useContext(SeoContext)
  if (!ctx) {
    throw new Error('useSeo debe usarse dentro de SeoProvider')
  }
  return ctx
}
